<template>
  <div class="editor">
    <div ref="toolbar"></div>
    <div ref="editor"></div>
  </div>
</template>

<script>
import Quill from 'quill';
import ImageResize from 'quill-image-resize-module';
import VideoResize from 'quill-video-resize-module';

// services
import FileService from '@/services/FileService';

// quill modules
import EventHandlerImageModule from './modules/EventHandlerImageModule';
import EventHandlerVideoModule from './modules/EventHandlerVideoModule';
import Image from './modules/Image';
import Video from './modules/Video';

export default {
  name: 'Editor',

  props: {
    value: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      changed: false,
      content: '',
      editor: null,
      isResizing: false,
    };
  },

  mounted() {
    const toolbarOptions = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link'],
      ['image'],
      ['video'],
      ['clean'],
    ];
    if (!this.editor) {
      Quill.register('modules/imageResize', ImageResize);
      Quill.register('modules/videoResize', VideoResize);
      Quill.register(Image);
      Quill.register(Video);

      this.editor = new Quill(this.$refs.editor, {
        theme: 'snow',
        modules: {
          toolbar: toolbarOptions,
          imageResize: {
            modules: [
              'Resize',
              'DisplaySize',
              'Toolbar',
              EventHandlerImageModule,
            ],
            vm: this,
          },
          videoResize: {
            modules: [
              'Resize',
              'DisplaySize',
              'Toolbar',
              EventHandlerVideoModule,
            ],
            vm: this,
          },
        },
        toolbar: {
          container: this.$refs.toolbar,
        },
        placeholder: 'Beitrag erstellen…',
      });
    }

    if (this.value && this.value !== '<p><br></p>') {
      this.editor.pasteHTML(this.value);
    }

    this.bindEvents();
  },

  watch: {
    value(newVal) {
      if (newVal && !this.editor.hasFocus()) {
        this.editor.pasteHTML(newVal);
      }
      if (!newVal) {
        this.editor.pasteHTML('');
      }
    },

    isResizing(newVal) {
      this.$emit('editor:ready', !newVal);
    },
  },

  methods: {
    bindEvents() {
      this.onEditorChangeProxy = this.onEditorChange.bind(this);
      this.editor.on('editor-change', this.onEditorChangeProxy);
      this.editor.getModule('toolbar').addHandler('image', () => {
        this.onSelectLocalImage();
      });

      this.editor.on('selection-change', this.onEditorChangeProxy);
    },

    insertToEditor(url) {
      const range = this.editor.getSelection();
      this.editor.insertEmbed(range.index, 'image', `${url}`);
    },

    onEditorChange() {
      const value = this.editor.scrollingContainer.innerHTML;
      if (value !== '<p><br></p>' && !this.isResizing) {
        this.$emit('input', value);
      }

      if (value === '<p><br></p>') {
        this.$emit('input', '');
      }
    },

    onUploadFile(files) {
      FileService.uploadImages(files).then((response) => {
        this.insertToEditor(response[0].file_url);
      });
    },

    onSelectLocalImage() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.click();
      input.onchange = () => {
        const file = input.files[0];
        if (/^image\//.test(file.type)) {
          this.onUploadFile([file]);
        } else {
          console.warn('You could only upload images.');
        }
      };
    },
  },
};
</script>

<style lang="scss">
@import "~quill/dist/quill.snow.css";

.editor {
  background-color: #fff;
  font-size: 1rem;

  .ql-editor {
    font-size: 1rem;
    font-family: "Gotham";

    iframe {
      pointer-events: none;
    }
  }

  &.is-invalid {
    .ql-toolbar,
    .ql-container {
      border-left-color: #f85359;
      border-right-color: #f85359;
    }
    .ql-toolbar {
      border-top-color: #f85359;
    }

    .ql-container {
      border-bottom-color: #f85359;
    }
  }
}
</style>
